<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';

import { useTimeoutFn } from '@vueuse/core';
import { useMotionAllowed } from '~kui/composables/useMotionPresets';
import { motion } from 'motion-v';

type GazeDirection = 'right' | 'down' | 'centered' | 'darting';

type Size = Extract<SizeVariants, 'md' | 'lg' | 'xl' | '2xl'>;

interface Props {
  gaze?: GazeDirection;
  size?: Size;
  layoutId?: string;
  hideEyes?: boolean;
  /** The app's own motion switch, on top of the OS preference. Off, the eyes hold still. */
  animated?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  gaze: 'centered',
  size: 'md',
  layoutId: '',
  hideEyes: false,
  animated: true,
});

/** Blinking runs for as long as Blinky is on screen, so it is the first thing a motion preference stills. */
const motionAllowed = useMotionAllowed(() => props.animated);
const still = computed(() => !motionAllowed.value);

const DEFAULT_BLINK_ANIMATE = { scaleY: [1, 0.1, 1] };
const DEFAULT_BLINK_TRANSITION = {
  duration: 0.15,
  repeat: Infinity,
  repeatDelay: 2,
};
const STILL_EYE_ANIMATE = { scaleY: 1 };
const STILL_TRANSITION = { duration: 0 };

const DARTING_DURATION = 3;
const DARTING_IDLE_DELAY_MS = 1000;

// Synced timeline shared by gaze (eye container) and blink (each eye):
//   center → look LEFT → blink ×1 → look RIGHT → blink ×1 → look UP → center → blink ×2
const DARTING_TIMES = [0, 0.15, 0.19, 0.23, 0.40, 0.44, 0.48, 0.65, 0.78, 0.82, 0.86, 0.90, 1];

const DARTING_GAZE = {
  x: ['0%', '-55%', '-55%', '-55%', '55%', '55%', '55%', '0%', '0%', '0%', '0%', '0%', '0%'],
  y: ['0%', '-10%', '-10%', '-10%', '-10%', '-10%', '-10%', '-50%', '0%', '0%', '0%', '0%', '0%'],
};

const DARTING_BLINK = {
  scaleY: [1, 1, 0.1, 1, 1, 0.1, 1, 1, 1, 0.1, 1, 0.1, 1],
};

const SIZE_VARIANTS = {
  container: { 'md': 'size-8', 'lg': 'size-12', 'xl': 'size-32', '2xl': 'size-48' },
  glow: { 'md': 'size-8', 'lg': 'size-12', 'xl': 'size-32', '2xl': 'size-[11rem]' },
  eyeGap: { 'md': 'gap-[5px]', 'lg': 'gap-[7px]', 'xl': 'gap-4', '2xl': 'gap-[24px]' },
} satisfies Record<string, Record<Size, string>>;

const EYE_SIZES: Record<Size, { w: number; h: number }> = {
  'md': { w: 3.152, h: 4.848 },
  'lg': { w: 4.727, h: 7.273 },
  'xl': { w: 12.4, h: 16.5 },
  '2xl': { w: 16, h: 24 },
};

const BLUR_AMOUNTS: Record<Size, string> = { 'md': '2px', 'lg': '3px', 'xl': '7px', '2xl': '10px' };

const containerClasses = computed(() =>
  twMerge(cva('', { variants: { size: SIZE_VARIANTS.container } })({ size: props.size as Size })),
);

const glowClasses = computed(() =>
  twMerge(cva('', { variants: { size: SIZE_VARIANTS.glow } })({ size: props.size as Size })),
);

const eyeGapClasses = computed(() =>
  twMerge(cva('', { variants: { size: SIZE_VARIANTS.eyeGap } })({ size: props.size as Size })),
);

const blurAmount = computed(() => BLUR_AMOUNTS[props.size as Size] ?? '2px');

const eyeDims = computed(() => EYE_SIZES[props.size as Size]);

const STATIC_GAZE: Record<Exclude<GazeDirection, 'darting'>, { x: string; y: string }> = {
  right: { x: '30%', y: '-10%' },
  down: { x: '0%', y: '70%' },
  centered: { x: '0%', y: '0%' },
};

const gazeAnimate = computed(() => {
  if (props.gaze === 'darting') {
    return still.value ? STATIC_GAZE.centered : DARTING_GAZE;
  }
  return STATIC_GAZE[props.gaze];
});

const gazeTransition = computed(() => {
  if (still.value) {
    return STILL_TRANSITION;
  }
  return props.gaze === 'darting'
    ? {
        duration: DARTING_DURATION,
        times: DARTING_TIMES,
        ease: 'easeOut' as const,
      }
    : { duration: 0.3, ease: 'easeOut' as const };
});

const dartingComplete = ref(props.gaze !== 'darting');

const { start: startIdleDelay, stop: stopIdleDelay } = useTimeoutFn(() => {
  if (props.gaze === 'darting') {
    dartingComplete.value = true;
  }
}, DARTING_IDLE_DELAY_MS, { immediate: false });

watch(() => props.gaze, (gaze) => {
  dartingComplete.value = gaze !== 'darting';
  if (gaze !== 'darting') {
    stopIdleDelay();
  }
});

const onGazeAnimationComplete = () => {
  if (props.gaze !== 'darting') {
    return;
  }
  startIdleDelay();
};

const isDarting = computed(() => props.gaze === 'darting' && !dartingComplete.value);

const eyeAnimate = computed(() => {
  if (still.value) {
    return STILL_EYE_ANIMATE;
  }
  return isDarting.value ? DARTING_BLINK : DEFAULT_BLINK_ANIMATE;
});

const eyeTransition = computed(() => {
  if (still.value) {
    return STILL_TRANSITION;
  }
  return isDarting.value
    ? {
        duration: DARTING_DURATION,
        times: DARTING_TIMES,
      }
    : DEFAULT_BLINK_TRANSITION;
});
</script>

<template>
  <motion.div
    :class="
      twMerge('relative flex items-center justify-center', containerClasses)
    "
    :layout="!!layoutId"
    :layout-id="layoutId || undefined"
    :transition="{ layout: { type: 'spring', stiffness: 280, damping: 28, mass: 1 } }"
    role="presentation"
  >
    <div
      :class="twMerge('absolute rounded-full bg-secondary transform-gpu', glowClasses)"
      :style="{ filter: `blur(${blurAmount})` }"
    />
    <motion.div
      v-if="!hideEyes"
      data-testid="blinky-gaze"
      :class="twMerge('relative flex items-center', eyeGapClasses)"
      :animate="gazeAnimate"
      :transition="gazeTransition"
      :on-animation-complete="onGazeAnimationComplete"
    >
      <motion.div
        v-for="eye in ['left', 'right']"
        :key="eye"
        data-testid="blinky-eye"
        :animate="eyeAnimate"
        :transition="eyeTransition"
      >
        <svg
          :width="eyeDims.w"
          :height="eyeDims.h"
          :viewBox="`0 0 ${eyeDims.w} ${eyeDims.h}`"
          xmlns="http://www.w3.org/2000/svg"
          overflow="visible"
        >
          <ellipse
            :cx="eyeDims.w / 2"
            :cy="eyeDims.h / 2"
            :rx="eyeDims.w / 2"
            :ry="eyeDims.h / 2"
            fill="#232323"
          />
        </svg>
      </motion.div>
    </motion.div>
  </motion.div>
</template>
